import { z } from 'zod';
import type { Brand } from './brand';

/**
 * Opaque identifiers.
 *
 * All ids are server-generated. The client may echo one back, never mint one -
 * with the single exception of {@link IdempotencyKey}, which the client
 * generates so a retried request cannot be applied twice.
 */
export type AccountId = Brand<string, 'AccountId'>;
export type ItemInstanceId = Brand<string, 'ItemInstanceId'>;
export type LedgerEntryId = Brand<string, 'LedgerEntryId'>;
export type IdempotencyKey = Brand<string, 'IdempotencyKey'>;

/** UUID v4/v7 shaped. Kept permissive enough for either generation strategy. */
export const uuidSchema = z.uuid();

export const accountIdSchema = uuidSchema.transform((value) => value as AccountId);
export const itemInstanceIdSchema = uuidSchema.transform((value) => value as ItemInstanceId);
export const ledgerEntryIdSchema = uuidSchema.transform((value) => value as LedgerEntryId);

/**
 * Client-supplied key that makes a mutating request replay-safe.
 *
 * The server stores the key together with the produced result. A second
 * request carrying the same key returns the stored result instead of running
 * the operation again. Required for gacha rolls, purchases and trades.
 */
export const idempotencyKeySchema = z
  .string()
  .min(16)
  .max(128)
  .regex(/^[A-Za-z0-9_-]+$/, 'idempotency key must be URL-safe')
  .transform((value) => value as IdempotencyKey);
